import type { DetectOverflowOptions } from '@floating-ui/react-dom';
import { arrow, autoUpdate, shift, useFloating } from '@floating-ui/react-dom';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import _ from 'lodash';
import type { HTMLAttributes, ReactNode } from 'react';
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import styles from './HoverPreviewable.module.scss';
import { SCREEN_EDGE_PADDING } from './ContentPreviewPopOver';
import FloatingArrow from './FloatingArrow';
import contentPreviewPopOverSize from '../utils/contentPreviewPopOverSize';

/**
 * Global state contains three phases:
 * 1. active:
 *    when the cursor is on the target
 * 2. transitioning:
 *    the intermediate state between active and idle.
 *    `activeId` is the id of the target that is currently active, or `null` if
 *    there is no active target.
 *    * shouldPreload: whether the next active target should be preloaded
 * 3. idle:
 *    no target is active
 */
type GlobalState =
  | {
      phase: 'active';
      activeId: string;
    }
  | {
      phase: 'transitioning';
      activeId: string | null;
      nextActiveId: string;
      shouldPreload: boolean;
    }
  | {
      phase: 'idle';
    };

/**
 * Component state is derived from the global state and the component id.
 * This prevents unnecessary re-renders of the component as
 * the global state changes are not relevant to most components.
 * 1. active:
 *    the preview should be visible
 * 2. leaving:
 *    the preview should be visible, but it should be hidden soon
 *    because the cursor has moved to another target
 * 3. will-enter:
 *    the preview should be visible soon but should not be loaded yet
 * 4. entering:
 *    the preview should be visible soon and should be loaded in the background
 *    to make the transition smooth
 * 5. idle:
 *    the preview should not be loaded
 */
type ComponentState = 'active' | 'leaving' | 'will-enter' | 'entering' | 'idle';
const getComponentState = (state: GlobalState, id: string): ComponentState => {
  if (state.phase === 'active' && state.activeId === id) {
    return 'active';
  }
  if (state.phase === 'transitioning') {
    if (state.nextActiveId === id) {
      return state.shouldPreload ? 'entering' : 'will-enter';
    }
    if (state.activeId === id) {
      return 'leaving';
    }
  }
  return 'idle';
};

const DETECT_OVERFLOW_OPTIONS: Partial<DetectOverflowOptions> = {
  padding: SCREEN_EDGE_PADDING,
};

interface HoverPreviewableProps {
  children: (
    props: Pick<HTMLAttributes<HTMLElement>, 'onMouseEnter' | 'onMouseLeave'>,
  ) => React.ReactNode;
  renderPreview: (args: {
    isOpen: boolean;
    updatePopOver: () => void;
  }) => ReactNode;
  alignToTop?: boolean;
  forcePreviewOpen?: boolean;
}

const HoverPreviewable = ({
  children,
  renderPreview,
  alignToTop,
  forcePreviewOpen,
}: HoverPreviewableProps) => {
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const middleware = useMemo(
    () => [
      shift(DETECT_OVERFLOW_OPTIONS),
      contentPreviewPopOverSize(DETECT_OVERFLOW_OPTIONS),
      arrow({ element: arrowRef, padding: 12 }),
    ],
    [],
  );

  const {
    refs,
    floatingStyles,
    elements,
    update,
    placement: actualPlacement,
    middlewareData: { arrow: arrowData },
  } = useFloating({
    placement: alignToTop ? 'right-start' : 'right',
    middleware,
    whileElementsMounted: (...args) =>
      autoUpdate(...args, { ancestorScroll: false }),
  });

  const { state: actualState, targetProps } = useHoverPreviewable({
    handleClose: (close) => {
      let clientX: number | null = null;
      const handler = _.throttle(
        (event: MouseEvent) => {
          const oldClientX = clientX;
          clientX = event.clientX;
          if (oldClientX === null) return;

          if (!elements.floating) return;
          const { left, right, top, bottom } =
            elements.floating.getBoundingClientRect();
          const { clientY } = event;
          const isInPopOver =
            clientX >= left &&
            clientX <= right &&
            clientY >= top &&
            clientY <= bottom;
          const oldDistance = left - oldClientX;
          const distance = left - clientX;
          const isGettingCloser = distance < oldDistance - 100;
          if (!isInPopOver && !isGettingCloser) {
            close();
          }
        },
        200,
        { leading: true },
      );

      document.addEventListener('mousemove', handler);
      return () => {
        handler.cancel();
        document.removeEventListener('mousemove', handler);
      };
    },
  });
  const state = forcePreviewOpen ? 'active' : actualState;

  const shouldShow = state === 'active' || state === 'leaving';
  const shouldRender = shouldShow || state === 'entering';
  const shouldShowRef = useRef(shouldShow);
  useLayoutEffect(() => {
    if (shouldShowRef.current !== shouldShow) {
      shouldShowRef.current = shouldShow;
      if (shouldShow) {
        update();
      }
    }
  }, [shouldShow, update]);

  return (
    <div className={styles.container}>
      {children(targetProps)}
      <div ref={refs.setReference} className={styles.reference} />
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          ...(shouldShow ? {} : { pointerEvents: 'none', opacity: 0 }),
        }}
        aria-hidden={!shouldShow}
        className={styles.previewContainer}
      >
        {shouldRender && (
          <>
            <div className={styles.preview}>
              {renderPreview({ isOpen: shouldShow, updatePopOver: update })}
            </div>
            <div className={styles.arrowContainer}>
              <FloatingArrow
                ref={arrowRef}
                placement={actualPlacement}
                arrowData={arrowData}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const PRELOAD_DURATION = 200;
const CANCEL_SKIP_DELAY_DURATION = 200;
const OPEN_DELAY_DURATION = 300;

const timerAtom = atom<ReturnType<typeof setTimeout> | null>(null);
const globalStateAtom = atom<GlobalState>({ phase: 'idle' });
const updateGlobalStateAtom = atom(
  null,
  (get, set, sourceId: string, shouldBecomeActive: boolean) => {
    set(timerAtom, (timer) => {
      if (timer) clearTimeout(timer);
      return null;
    });

    const state = get(globalStateAtom);
    if (!shouldBecomeActive) {
      if (state.phase === 'transitioning') {
        set(
          timerAtom,
          setTimeout(() => {
            set(globalStateAtom, { phase: 'idle' });
          }, CANCEL_SKIP_DELAY_DURATION),
        );
      } else {
        set(globalStateAtom, { phase: 'idle' });
      }
      return;
    }

    const transitioningState = {
      phase: 'transitioning',
      activeId: state.phase === 'idle' ? null : state.activeId,
      nextActiveId: sourceId,
    } as const satisfies Partial<GlobalState>;

    set(globalStateAtom, { ...transitioningState, shouldPreload: false });
    set(
      timerAtom,
      setTimeout(
        () => {
          set(globalStateAtom, { ...transitioningState, shouldPreload: true });
          set(
            timerAtom,
            setTimeout(() => {
              set(globalStateAtom, { phase: 'active', activeId: sourceId });
              set(timerAtom, null);
            }, PRELOAD_DURATION),
          );
        },
        state.phase === 'idle' ||
          (state.phase === 'transitioning' && !state.activeId)
          ? OPEN_DELAY_DURATION
          : 10 /* A small delay to avoid unnecessary preload when cursor moves quickly across targets */,
      ),
    );
  },
);

interface UseHoverPreviewableArgs {
  handleClose: (close: () => void) => () => void;
}

// expose the hook for testing
export const useHoverPreviewable = ({
  handleClose,
}: UseHoverPreviewableArgs) => {
  const id = useId();

  const updateGlobalState = useSetAtom(updateGlobalStateAtom);

  const setIsActive = useCallback(
    (isActive: boolean) => updateGlobalState(id, isActive),
    [id, updateGlobalState],
  );

  const componentStateAtom = useMemo(
    () => atom((get) => getComponentState(get(globalStateAtom), id)),
    [id],
  );
  const state = useAtomValue(componentStateAtom);
  // Used in event handlers to avoid getting stale state.
  // This is needed because state set in `onMouseEnter()` may
  // not be read in `onMouseLeave()` sometimes when the cursor leaves quickly.
  const getLatestState = useAtomCallback(
    useCallback((get) => get(componentStateAtom), [componentStateAtom]),
  );

  const cleanupHandleCloseRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    return () => {
      cleanupHandleCloseRef.current?.();
      cleanupHandleCloseRef.current = null;
    };
  }, [state]);

  const onMouseEnter = () => {
    const latestState = getLatestState();
    if (latestState === 'idle' || latestState === 'leaving') {
      setIsActive(true);
    }
  };

  const onMouseLeave = () => {
    const latestState = getLatestState();
    if (latestState === 'will-enter' || latestState === 'entering') {
      setIsActive(false);
    } else if (latestState === 'active') {
      cleanupHandleCloseRef.current?.();
      cleanupHandleCloseRef.current = handleClose(() => {
        setIsActive(false);
      });
    }
  };

  return { state, targetProps: { onMouseEnter, onMouseLeave } };
};

export default HoverPreviewable;
